import { environment } from "../environments/environment";
import { authenticatedGuardBuilder } from "./core/guards/authenticated/authenticated.guard";
import { profileCompleteGuard } from "./core/guards/profile-complete/profile-complete.guard";
import { roleGuard } from "./core/guards/role/role.guard";
import {
  BREADCRUMB_FORCE_URL_KEY,
  BREADCRUMB_ROUTE_KEY
} from "./core/modules/ui-kit/types/breadcrumb.type";
import { WaveupRoute } from "./core/types/waveup-routes.types";
import { landingGuard } from "./core/guards/landing/landing.guard";
import { ROUTE } from "./shared/utils/routes";
import { radarGuard } from "./core/guards/radar/radar.guard";

export const routes: WaveupRoute[] = [
  {
    path: "sva",
    data: {
      [BREADCRUMB_ROUTE_KEY]: "companies.dashboard",
      [BREADCRUMB_FORCE_URL_KEY]: {
        // Force tem to go to user dashboard instead of home
        tem: ROUTE.COMPANIES_DASHBOARD,
        "tem-manager": ROUTE.COMPANIES_DASHBOARD
      }
    },
    canActivate: [
      authenticatedGuardBuilder({ mustBeAuthenticated: true }),
      profileCompleteGuard
    ],
    // `canActivate` sul padre gira una volta sola, quando si entra nella shell:
    // spostandosi fra le pagine figlie non viene più rivalutato, e un account a
    // profilo incompleto che avesse superato il primo controllo poteva poi
    // navigare ovunque. `canActivateChild` gira a ogni navigazione interna, che
    // è ciò che serve per tenerlo dentro al wizard.
    canActivateChild: [profileCompleteGuard],
    loadComponent: () =>
      import(
        "./core/components/root-authenticated/root-authenticated.component"
      ).then((x) => x.RootAuthenticatedComponent),
    children: [
      {
        // Dove si atterra: di regola la dashboard, ma in prova — dove la
        // dashboard è chiusa — il Radar, se c'è. La guardia reindirizza
        // sempre, quindi questa rotta non mostra mai niente.
        path: "",
        pathMatch: "full",
        canActivate: [landingGuard],
        children: []
      },
      {
        path: "dashboard",
        canActivate: [roleGuard],
        data: {
          roles: ["admin", "enterprise-admin"]
        },
        loadChildren: () =>
          import("./features/dashboard/dashboard.routes").then((x) => x.routes)
      },
      {
        path: "users",
        canActivate: [roleGuard],
        data: {
          roles: [
            "admin",
            "enterprise-admin",
            "enterprise-association-admin",
            "enterprise-association-tem"
          ]
        },
        loadChildren: () =>
          import("./features/users/users.routes").then((x) => x.routes)
      },
      {
        path: "users/tem-client",
        canActivate: [roleGuard],
        data: {
          roles: ["tem"]
        },
        loadChildren: () =>
          import("./features/tem-client/tem-client.routes").then(
            (x) => x.routes
          )
      },
      {
        path: "tem/tem-manager",
        canActivate: [roleGuard],
        data: {
          roles: ["admin", "enterprise-admin", "enterprise-association-admin"]
        },
        loadChildren: () =>
          import("./features/tem-manager/tem-manager.routes").then(
            (x) => x.routes
          )
      },
      {
        path: "sales",
        canActivate: [roleGuard],
        data: {
          roles: ["admin"]
        },
        loadChildren: () =>
          import("./features/sales/sales.routes").then((x) => x.routes)
      },

      {
        path: "packages",
        canActivate: [roleGuard],
        data: {
          // roles: ["user"]
        },
        loadChildren: () =>
          import("./features/packages/packages.routes").then((x) => x.routes)
      },

      {
        path: "companies",
        canActivate: [roleGuard],
        data: {
          roles: ["user", "sales"]
        },
        loadChildren: () =>
          import("./features/companies/companies.routes").then((x) => x.routes)
      },
      {
        path: "settings",
        loadChildren: () =>
          import("./features/settings/settings.routes").then((x) => x.routes)
      },
      {
        path: "notifications",
        data: {
          [BREADCRUMB_ROUTE_KEY]: "notifications.notifications"
        },
        loadChildren: () =>
          import("./features/notifications/notifications.routes").then(
            (x) => x.routes
          )
      },
      {
        path: "enterprises",
        data: {
          [BREADCRUMB_ROUTE_KEY]: "enterprises-list.title"
        },
        loadChildren: () =>
          import("./features/enterprises/enterprises.routes").then(
            (x) => x.routes
          )
      },
      {
        path: "history",
        loadChildren: () =>
          import("./features/history/history.routes").then((x) => x.routes)
      },
      {
        path: "apollo",
        loadChildren: () =>
          import("./features/apollo/apollo.routes").then((x) => x.routes)
      },
      {
        path: "checkup",
        data: {
          [BREADCRUMB_ROUTE_KEY]: "export.intelligence"
        },
        loadChildren: () =>
          import("./features/checkup/checkup.routes").then((x) => x.routes)
      },
      {
        path: "sector-analysis",
        data: {
          [BREADCRUMB_ROUTE_KEY]: "export.intelligence"
        },
        loadChildren: () =>
          import("./features/sector-analysis/sector-analysis.routes").then(
            (x) => x.routes
          )
      },
      {
        /*
         * La vetrina di Export Intelligence: si apre cliccando la voce di
         * menu, che prima apriva soltanto il sottomenu.
         *
         * Senza guardie e senza pacchetto richiesto, di proposito: è la
         * pagina che spiega che cosa c'è nella sezione e che cosa serve per
         * averlo, quindi deve aprirsi anche — soprattutto — a chi due delle
         * tre analisi non le ha.
         */
        path: "export-intelligence",
        data: {
          [BREADCRUMB_ROUTE_KEY]: "export.intelligence"
        },
        loadChildren: () =>
          import(
            "./features/export-intelligence/export-intelligence.routes"
          ).then((x) => x.routes)
      },
      {
        // Radar Mercati: sta dentro a Export Intelligence, ed è la pagina su
        // cui finisce la registrazione di chi si iscrive da solo. Per questo
        // la apre solo chi quel giro l'ha fatto: gli altri un Radar non ce
        // l'hanno, e troverebbero una pagina vuota.
        path: "radar-mercati",
        canActivate: [radarGuard],
        data: {
          [BREADCRUMB_ROUTE_KEY]: "export.intelligence"
        },
        loadChildren: () =>
          import("./features/radar-markets/radar-markets.routes").then(
            (x) => x.routes
          )
      },
      {
        path: "calendar",
        loadChildren: () =>
          import("./features/calendar/calendar.routes").then((x) => x.routes)
      },
      {
        // Onboarding of a self-service signup. It lives inside the
        // authenticated shell and therefore under the guard, which lets this
        // one url through on purpose: redirecting the wizard onto itself is
        // how a redirect loop starts.
        path: "onboarding",
        loadChildren: () =>
          import("./features/onboarding/onboarding.routes").then(
            (x) => x.routes
          )
      },
      {
        path: "back-office",
        canActivate: [roleGuard],
        data: {
          roles: ["admin"]
        },
        loadChildren: () =>
          import("./features/back-office/back-office.routes").then(
            (x) => x.routes
          )
      },
      {
        path: "gemini-usage",
        canActivate: [roleGuard],
        data: {
          roles: ["admin"]
        },
        loadChildren: () =>
          import("./features/gemini-usage/gemini-usage.routes").then(
            (x) => x.routes
          )
      },
      // development tool comparing Claude and Gemini (WWP-41): the route
      // exists only in the builds where the flag is on, dev and local
      ...(environment.enableAiPlayground
        ? [
            {
              path: "ai-playground",
              canActivate: [roleGuard],
              data: {
                roles: ["admin"]
              },
              loadChildren: () =>
                import("./features/ai-playground/ai-playground.routes").then(
                  (x) => x.routes
                )
            } satisfies WaveupRoute
          ]
        : [])
    ]
  },
  {
    path: "auth",
    /* canActivate: [authenticatedGuardBuilder({ mustBeAuthenticated: false })], */
    loadChildren: () =>
      import("./features/auth/auth.routes").then((x) => x.routes)
  },
  // La diagnostica del billing (LOCAL-STACK.md): licenza, abbonamento e
  // pacchetti in vendita su una schermata sola, senza passare dal login. Il
  // commento diceva "solo sviluppo locale" e la rotta veniva registrata
  // comunque in ogni build: adesso la condizione c'è davvero. Le chiamate che
  // fa sono protette da Kong, quindi non era una fuga di dati — era una pagina
  // che non ha ragione di esistere in un ambiente distribuito.
  ...(environment.enableDiagnostics
    ? [
        {
          path: "billing-diagnostics",
          loadComponent: () =>
            import(
              "./features/billing-diagnostics/billing-diagnostics.component"
            ).then((x) => x.BillingDiagnosticsComponent)
        }
      ]
    : []),
  { path: "**", redirectTo: "sva" }
];
